/**
 * Staff authentication, against the real endpoints in `kernel/identity/views.py`.
 *
 * Everything here passes `exact: true`, because these routes are registered with
 * `registry.add_path` rather than generated by a router. Two consequences, and they
 * always travel together:
 *
 *   - the paths are ABSOLUTE (`/api/auth/staff/login`), so the audience root is not
 *     prepended — prepending it produces `/api/staff/api/auth/staff/login`, which is
 *     a 404 that looks exactly like a permissions problem until you read the URL;
 *   - they take NO TRAILING SLASH, unlike every router resource.
 *
 * See the header of `endpoints.ts`.
 *
 * Three behaviours of the backend are worth knowing before you write a login screen:
 *
 * 1. A FAILED LOGIN SAYS NOTHING. Wrong password, unknown address, suspended account
 *    and "no staff membership anywhere" all return the same 401 with the same detail.
 *    Do not try to be more helpful in the UI — the uniformity is what stops the login
 *    form being an account-enumeration oracle, and for a legal product the mere fact
 *    that an address belongs to a firm is disclosive.
 *
 * 2. THE ORGANIZATION CHOICE IS A 200, NOT AN ERROR. A user with several active
 *    memberships gets `{ organization_choice_required: true, memberships: [...] }`
 *    with a 200. The credentials were right; the system needs to be told which firm
 *    and will not guess. Log in again with `organization_id` set.
 *
 * 3. LOGIN RETURNS A BARE SESSION, NOT A USER. Fetch `me` after logging in.
 */

import { type ApiClient } from './client';
import { EXACT_PATHS } from './endpoints';
import type { StoredSession } from './session';

export interface LoginCredentials {
  email: string;
  password: string;
  /**
   * Selects among memberships the caller ALREADY holds. It can never introduce a
   * tenant — the backend validates it against the user's own memberships (BR-02).
   */
  organization_id?: string;
  device_label?: string;
}

/** What the server issues. Note: `token`, not `access_token`. */
export interface IssuedSession {
  token: string;
  token_type: 'Bearer';
  audience: 'staff' | 'portal' | 'platform' | 'token';
  expires_at: string;
  session_id: string;
}

export interface MembershipOption {
  organization_id: string;
  organization_name: string;
  role_key: string;
}

export interface OrganizationChoiceRequired {
  organization_choice_required: true;
  memberships: MembershipOption[];
}

export type LoginResult = IssuedSession | OrganizationChoiceRequired;

export function needsOrganizationChoice(
  result: LoginResult,
): result is OrganizationChoiceRequired {
  return 'organization_choice_required' in result;
}

/** `GET /api/staff/me` — `services.describe_session`. */
export interface SessionDescription {
  user: {
    id: string;
    email: string;
    full_name: string;
    phone: string;
    status: 'invited' | 'active' | 'suspended' | 'closed';
    mfa_enabled: boolean;
  };
  organization: {
    id: string;
    name: string;
    slug: string;
    timezone: string;
    base_currency: string;
    locale: string;
    /** What this firm calls a matter. Render this, not a hardcoded "Case". */
    matter_label: string;
    /** Set only for a self-serve demo firm: when its free period ends. */
    demo_expires_at?: string | null;
  } | null;
  membership: {
    id: string;
    role_key: string;
    status: string;
    department_id: string | null;
    office_id: string | null;
  } | null;
  role: string;
  /**
   * The effective `module.action.scope` keys for THIS membership, including any live
   * break-glass grant. The frontend renders from it; the backend re-checks every one
   * on execution. The payload is a convenience, never the control.
   */
  permissions: string[];
  audience: string;
  session: { id: string; expires_at: string };
}

function toStoredSession(issued: IssuedSession, organizationId: string | null): StoredSession {
  return {
    accessToken: issued.token,
    expiresAt: Date.parse(issued.expires_at) || null,
    organizationId,
  };
}

/**
 * Sign in. Anonymous — there is no session yet, and login is mounted outside every
 * authenticated root for exactly that reason.
 */
export async function login(
  client: ApiClient,
  credentials: LoginCredentials,
): Promise<LoginResult> {
  return client.post<LoginResult>(EXACT_PATHS.staffLogin, credentials, {
    anonymous: true,
    exact: true,
  });
}

/**
 * Sign a CLIENT in, and keep the session.
 *
 * Separate from `loginAndStore` and deliberately simpler: a contact belongs to exactly
 * one firm, resolved server-side from their identity link, so there is no organization
 * to choose and no `organization_id` to send. A portal login that offered a firm picker
 * would be asking the client a question only the firm can answer.
 */
export async function portalLoginAndStore(
  client: ApiClient,
  credentials: { email: string; password: string },
): Promise<StoredSession> {
  const issued = await client.post<IssuedSession>(EXACT_PATHS.portalLogin, credentials, {
    anonymous: true,
    exact: true,
  });
  const session = toStoredSession(issued, null);
  client.session.write(session);
  return session;
}

/** Sign in and persist the session in one step. Returns null if a choice is needed. */
export async function loginAndStore(
  client: ApiClient,
  credentials: LoginCredentials,
): Promise<{ session: StoredSession } | { choices: MembershipOption[] }> {
  const result = await login(client, credentials);
  if (needsOrganizationChoice(result)) return { choices: result.memberships };

  const session = toStoredSession(result, credentials.organization_id ?? null);
  client.session.write(session);
  return { session };
}

/**
 * Redeem a staff invitation and keep the session it returns.
 *
 * Beside `loginAndStore` because it is the same act: a credential is exchanged for a
 * session and the session is written to the same store. Doing it here rather than in
 * the page keeps one place that knows how a session is shaped and where it lives.
 *
 * The new member lands inside the application already signed in. Making somebody
 * choose a password and then immediately type it again is a pointless step at the
 * exact moment they are forming an impression of the product.
 */
export async function acceptInvitationAndStore(
  client: ApiClient,
  input: { token: string; password: string; full_name?: string },
): Promise<StoredSession> {
  const result = await client.post<LoginResult>(EXACT_PATHS.invitationAccept, input, {
    anonymous: true,
    exact: true,
  });
  const session = toStoredSession(result as never, null);
  client.session.write(session);
  return session;
}

/**
 * The address an invitation link is for. Looking does not use the link up, and a link
 * that is used, expired or never existed is refused exactly as accepting it would be.
 */
export async function invitationDetails(
  client: ApiClient,
  token: string,
): Promise<{ email: string; full_name: string }> {
  return client.post<{ email: string; full_name: string }>(
    EXACT_PATHS.invitationDetails,
    { token },
    { anonymous: true, exact: true },
  );
}

/**
 * Ask for a demo firm. Nothing is signed in: the person receives an email with a link
 * that lets them choose a password, which is also how the address is proved theirs.
 */
export async function requestDemo(
  client: ApiClient,
  input: { full_name: string; email: string; firm_name?: string },
): Promise<void> {
  await client.post(EXACT_PATHS.demoSignup, input, { anonymous: true, exact: true });
}

/** The Android build offered at /app, or `available: false` before one is published. */
export type AndroidRelease =
  | { available: false }
  | {
      available: true;
      version: string;
      size_bytes: number;
      sha256: string;
      published_at: string;
    };

export async function androidRelease(client: ApiClient): Promise<AndroidRelease> {
  return client.get<AndroidRelease>(EXACT_PATHS.appAndroid, { anonymous: true, exact: true });
}

/**
 * Redeem a client's portal invitation.
 *
 * Returns nothing, because the endpoint issues no session -- and that is deliberate on
 * the server. A client's tenant is resolved at portal login from their identity links,
 * not from the invitation token, and a contact holding access to more than one firm's
 * portal has to be asked which. So they set a password here and sign in next.
 */
export async function acceptPortalInvitation(
  client: ApiClient,
  input: { token: string; password: string },
): Promise<void> {
  await client.post(EXACT_PATHS.portalInvitationAccept, input, {
    anonymous: true,
    exact: true,
  });
}

/**
 * What a redeemed link comes back as.
 *
 * `scope` is the whole of the holder's authority: this session reaches that one object
 * and nothing else. The audience is `token`, which every other API root refuses with a
 * 401 before any view is reached.
 */
export interface RedeemedLink extends IssuedSession {
  audience: 'token';
  scope: { type: string; id: string };
}

/**
 * Redeem a single-purpose link.
 *
 * DELIBERATELY DOES NOT STORE THE SESSION, which is why there is no `redeemLinkAndStore`
 * beside `loginAndStore` and `portalLoginAndStore`. Two reasons, and both matter:
 *
 *   The portal app has one session store. Writing a link token into it would evict the
 *   real session of a client who happens to be signed in on the same browser -- logging
 *   them out and replacing their access with a credential good for one agreement.
 *
 *   The token is in the URL. Persisting it buys nothing, and leaves a live credential in
 *   local storage on what may well be a shared device.
 *
 * The caller holds the returned token for as long as the page is open and no longer.
 */
export async function redeemLink(
  client: ApiClient,
  token: string,
): Promise<RedeemedLink> {
  return client.post<RedeemedLink>(
    EXACT_PATHS.linkRedeem,
    { token },
    { anonymous: true, exact: true },
  );
}

export function fetchMe(client: ApiClient): Promise<SessionDescription> {
  return client.get<SessionDescription>(EXACT_PATHS.staffMe, { exact: true });
}

/**
 * Exchange the current session for a fresh token. Wire this into `ApiClientConfig.refresh`
 * so a 401 is retried once transparently rather than dumping the user at the login
 * screen mid-form.
 */
export async function refreshSession(
  client: ApiClient,
  current: StoredSession,
): Promise<StoredSession | null> {
  try {
    const issued = await client.post<IssuedSession>(EXACT_PATHS.staffRefresh, undefined, {
      exact: true,
      // Not `_retried`, but the effect is the same: a failed refresh must not
      // recurse into another refresh.
      _retried: true,
    });
    return toStoredSession(issued, current.organizationId);
  } catch {
    return null;
  }
}

/**
 * Sign out. Revokes the session server-side, then clears it locally.
 *
 * The local clear happens even if the request fails: a user who pressed "sign out"
 * on a shared machine must not still be signed in because the network was down.
 */
export async function logout(client: ApiClient): Promise<void> {
  try {
    await client.post(EXACT_PATHS.staffLogout, undefined, { exact: true });
  } catch {
    // Intentionally swallowed. See above.
  } finally {
    client.session.clear();
  }
}

/** Sign out of every session on every surface. Use after a suspected compromise. */
export async function logoutEverywhere(client: ApiClient): Promise<number> {
  try {
    const result = await client.post<{ revoked: number }>(
      EXACT_PATHS.staffLogoutAll,
      undefined,
      { exact: true },
    );
    return result.revoked;
  } finally {
    client.session.clear();
  }
}
